import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadCloud, CheckCircle, XCircle, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import LoadingSpinner from '../components/Shared/LoadingSpinner';
import PageHeader from '../components/Shared/PageHeader';
import {
  fmtMoney, fmtNumber, fmtDate, fmtCyds, fmtRate, fmtPct, costCenterName,
} from '../components/Sales/format';
import { c, btn, card } from '../theme';

const LAYER_LABELS = {
  extract_file: 'Extract File', cross_over: 'Cross Over File', city_mapping: 'City Mapping', unresolved: 'Unresolved',
};

export default function SalesUpload() {
  const qc = useQueryClient();
  const [file, setFile]         = useState(null);
  const [asOfDate, setAsOfDate] = useState('');
  const [progress, setProgress] = useState(0);
  const [preview, setPreview]   = useState(null);   // { batch_id, preview }
  const [needsMappings, setNeedsMappings] = useState(false);
  const inputRef = useRef(null);

  const { data: batches = [], isLoading: batchesLoading } = useQuery({
    queryKey: ['sales-batches'],
    queryFn:  () => api.get('/sales-report/batches'),
    refetchInterval: 15000,
  });

  const uploadMutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      // The date before the file: multer reads fields in arrival order.
      if (asOfDate) fd.append('as_of_date', asOfDate);
      fd.append('file', file);
      return api.upload('/sales-report/upload', fd, setProgress);
    },
    onSuccess: (data) => { setPreview(data); setProgress(0); setNeedsMappings(false); },
    onError: (err) => {
      setProgress(0);
      setNeedsMappings(err.status === 409);
      toast.error(err.message || 'Upload failed.');
    },
  });

  const confirmMutation = useMutation({
    mutationFn: () => api.commit('/sales-report/upload/confirm', { batch_id: preview.batch_id }),
    onSuccess: (data) => {
      toast.success(`Committed the ${fmtDate(data.asOfDate)} export — ${fmtNumber(data.rowCount)} lines.`);
      resetAll();
      for (const key of ['sales-batches', 'sales-periods', 'sales-summary', 'sales-trend']) {
        qc.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (err) => toast.error(err.message || 'Confirm failed.'),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post('/sales-report/upload/cancel', { batch_id: preview.batch_id }),
    onSuccess: () => { toast('Upload discarded.'); resetAll(); qc.invalidateQueries({ queryKey: ['sales-batches'] }); },
    onError: (err) => toast.error(err.message || 'Cancel failed.'),
  });

  function resetAll() {
    setFile(null); setPreview(null); setProgress(0); setAsOfDate('');
    if (inputRef.current) inputRef.current.value = '';
  }

  const p = preview?.preview;
  const busy = uploadMutation.isPending || confirmMutation.isPending || cancelMutation.isPending;

  return (
    <div style={s.page}>
      <PageHeader
        eyebrow="Administration"
        title="Sales Upload"
        subtitle="Import an Open Market Commercial sales export from Softpak. It is resolved to cost centers and previewed first — nothing reaches the Sales Report until you confirm. Each export is one period on the trend; a second export for the same date replaces the first."
      />

      {!preview && (
        <div style={s.card}>
          <div
            style={{ ...s.dropZone, borderColor: file ? '#86EFAC' : c.borderStrong }}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
          >
            <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                   onChange={(e) => setFile(e.target.files[0] || null)} />
            <UploadCloud size={26} color={file ? c.success : c.textFaint} />
            <div style={s.dropText}>
              {file ? (
                <><strong style={{ color: c.success }}>{file.name}</strong><br /><span style={{ color: c.textFaint }}>{(file.size / 1024 / 1024).toFixed(1)} MB</span></>
              ) : (
                <><strong>Click to select</strong> or drag &amp; drop<br /><span style={{ color: c.textFaint }}>Open Market sales export, .xlsx or .xls</span></>
              )}
            </div>
          </div>

          <label style={s.field}>
            <span style={s.fieldLabel}>Export as-of date</span>
            <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)} style={s.dateInput} />
            <span style={s.fieldHint}>
              The export carries no date of its own. Leave blank to read it from the filename
              (e.g. OpenMarketSalesReport_9_16_26.xlsx); set it when the name has none.
            </span>
          </label>

          {uploadMutation.isPending && (
            <div style={s.progressWrap}>
              <div style={s.progressTrack}><div style={{ ...s.progressFill, width: `${progress}%` }} /></div>
              <span style={s.progressLabel}>{progress < 100 ? `${progress}%` : 'Resolving…'}</span>
            </div>
          )}

          {needsMappings && (
            <div style={s.warnBlock}>
              <div style={s.warnHead}><AlertTriangle size={14} /> Mapping files are missing</div>
              <div style={s.warnFoot}>
                Cost Center resolution needs all three. <Link to="/admin/sales-mappings">Upload them in Sales Mappings</Link>, then try again.
              </div>
            </div>
          )}

          <button
            style={{ ...s.primaryBtn, opacity: file && !busy ? 1 : 0.5 }}
            disabled={!file || busy}
            onClick={() => uploadMutation.mutate()}
          >
            {uploadMutation.isPending ? 'Parsing…' : 'Parse & Preview'}
          </button>
        </div>
      )}

      {p && (
        <div style={s.card}>
          <div style={s.previewHead}>
            <FileSpreadsheet size={18} color={c.navy} />
            <span style={s.previewTitle}>Preview — {p.file_name}</span>
            <span style={s.chip}>
              As of {fmtDate(p.as_of_date)} · {p.as_of_date_source === 'filename' ? 'read from the filename' : 'set on upload'}
            </span>
          </div>

          <div style={s.statGrid}>
            <Stat label="Lines" value={fmtNumber(p.total_lines)} />
            <Stat label="Total Revenue" value={fmtMoney(p.totals.revenue)} />
            <Stat label="Monthly Cyds" value={fmtCyds(p.totals.volume)} />
            <Stat label="Total Rev$/Cyd" value={fmtRate(p.totals.rev_per_cyd)} />
            <Stat label="Participation" value={fmtPct(p.totals.accounts ? p.totals.participating / p.totals.accounts : null)} />
            <Stat label="Unresolved Lines" value={fmtNumber(p.unresolved_lines)} accent={p.unresolved_lines ? c.warning : undefined} />
          </div>

          {p.warning_count > 0 && (
            <div style={s.warnBlock}>
              <div style={s.warnHead}><AlertTriangle size={14} /> Read before confirming</div>
              <ul style={s.warnList}>
                {p.warnings.map((w) => <li key={w} style={s.warnItem}>{w}</li>)}
              </ul>
              {p.unresolved_routes?.length > 0 && (
                <div style={s.warnFoot}>
                  Unresolved company/route: {p.unresolved_routes.map((u) => `${u.company} ${u.route || '(blank)'} ×${u.lines}`).join(', ')}
                </div>
              )}
            </div>
          )}

          <div style={s.subBlock}>
            <div style={s.subLabel}>By Cost Center</div>
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Cost Center', 'Accounts', 'Participating', 'Revenue', 'Monthly Cyds', 'Rev$/Cyd'].map((h, i) => (
                      <th key={h} style={i ? s.thR : s.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {p.by_cost_center.map((r) => (
                    <tr key={r.cost_center ?? 'unresolved'} style={s.tr}>
                      <td style={{ ...s.td, ...(r.cost_center === null ? { color: c.warningText, fontStyle: 'italic' } : null) }}>
                        {costCenterName(r.cost_center)}
                        {r.cost_center !== null && !r.linked && <span style={s.unlinked}> not a portal cost center</span>}
                      </td>
                      <td style={s.tdR}>{fmtNumber(r.accounts)}</td>
                      <td style={s.tdR}>{fmtNumber(r.participating)}</td>
                      <td style={s.tdR}>{fmtMoney(r.revenue)}</td>
                      <td style={s.tdR}>{fmtCyds(r.volume)}</td>
                      <td style={s.tdR}>{fmtRate(r.rev_per_cyd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={s.subBlock}>
            <div style={s.subLabel}>How it was resolved</div>
            <div style={s.chips}>
              {Object.entries(p.resolution_by_layer || {}).map(([layer, n]) => (
                <span key={layer} style={s.chip}>{LAYER_LABELS[layer] || layer} · {fmtNumber(n)} lines</span>
              ))}
              <span style={s.chip}>Participation · {p.participation_basis}</span>
              <span style={s.chip}>Shared containers split · {fmtNumber(p.shared_lines_split)} lines</span>
              {p.excluded_lines > 0 && <span style={s.chip}>Excluded · {fmtNumber(p.excluded_lines)} lines</span>}
            </div>
            <div style={s.versions}>
              Mapping files used:{' '}
              {Object.values(p.mapping_versions || {}).map((v) => `${v.file_name} (${fmtDate(v.uploaded_at)})`).join(' · ')}
            </div>
          </div>

          <div style={s.actionRow}>
            <button style={{ ...s.primaryBtn, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => confirmMutation.mutate()}>
              <CheckCircle size={15} /> {confirmMutation.isPending ? 'Committing…' : (p.replaces_batch ? 'Confirm & Replace' : 'Confirm & Commit')}
            </button>
            <button style={{ ...s.secondaryBtn, opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => cancelMutation.mutate()}>
              <XCircle size={15} /> Cancel
            </button>
          </div>
        </div>
      )}

      <div style={s.logSection}>
        <div style={s.logTitle}>Recent Imports</div>
        {batchesLoading ? <LoadingSpinner /> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>{['File', 'Source', 'Status', 'As Of', 'Latest', 'Lines', 'Unresolved', 'Revenue', 'By', 'When'].map((h) => <th key={h} style={s.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} style={s.tr}>
                    <td style={s.td} title={b.file_name}>{b.file_name?.slice(-44)}</td>
                    <td style={s.td}>{b.source === 'ftp' ? 'SFTP' : 'Manual'}</td>
                    <td style={s.td}><span style={{ ...s.statusPill, ...(STATUS_STYLE[b.status] || STATUS_STYLE.preview) }}>{b.status}</span></td>
                    <td style={s.td}>{fmtDate(b.as_of_date)}</td>
                    <td style={s.td}>{b.is_current ? '✓' : ''}</td>
                    <td style={s.tdR}>{fmtNumber(b.row_count)}</td>
                    <td style={s.tdR}>{fmtNumber(b.unresolved_row_count)}</td>
                    <td style={s.tdR}>{b.revenue === null ? '—' : fmtMoney(b.revenue)}</td>
                    <td style={s.td}>{b.uploaded_by_name || (b.source === 'ftp' ? 'System' : '—')}</td>
                    <td style={s.td}>{b.created_at ? new Date(b.created_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                {!batches.length && <tr><td colSpan={10} style={s.emptyLog}>No imports yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={s.stat}>
      <div style={{ ...s.statVal, ...(accent ? { color: accent } : {}) }}>{value}</div>
      <div style={s.statLabel}>{label}</div>
    </div>
  );
}

const STATUS_STYLE = {
  committed: { color: c.successText, background: c.successTint },
  preview:   { color: c.info,        background: c.infoTint },
  cancelled: { color: c.textFaint,   background: c.canvas },
};

const s = {
  page:        { maxWidth: '1040px', margin: '0 auto', paddingBottom: '8px' },
  card:        { ...card.sm, padding: '16px', marginBottom: '14px' },
  dropZone:    { borderWidth: '2px', borderStyle: 'dashed', borderRadius: '10px', padding: '30px', textAlign: 'center', cursor: 'pointer', marginBottom: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' },
  dropText:    { fontSize: '12.5px', color: c.textStrong, lineHeight: 1.6 },
  field:       { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px', maxWidth: '520px' },
  fieldLabel:  { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted },
  fieldHint:   { fontSize: '10.5px', color: c.textFaint, lineHeight: 1.45 },
  dateInput:   { height: '30px', fontSize: '12px', fontFamily: 'inherit', padding: '0 8px', border: `1px solid ${c.border}`, borderRadius: '6px', width: '170px' },
  progressWrap:{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
  progressTrack:{ flex: 1, height: '5px', background: c.border, borderRadius: '4px', overflow: 'hidden' },
  progressFill:{ height: '100%', background: c.navy, borderRadius: '4px', transition: 'width 0.2s' },
  progressLabel:{ fontSize: '10.5px', fontWeight: 600, color: c.textStrong, minWidth: '64px' },
  primaryBtn:  { ...btn.primary },
  secondaryBtn:{ ...btn.secondary },
  previewHead: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  previewTitle:{ fontSize: '13px', fontWeight: 600, color: c.text },
  statGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(128px, 1fr))', gap: '10px', marginBottom: '16px' },
  stat:        { ...card.sunk, padding: '11px 13px' },
  statVal:     { fontSize: '18px', fontWeight: 700, color: c.text, fontVariantNumeric: 'tabular-nums' },
  statLabel:   { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, marginTop: '3px' },
  subBlock:    { marginBottom: '18px' },
  subLabel:    { fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, marginBottom: '8px' },
  chips:       { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  chip:        { fontSize: '11px', color: c.textStrong, background: '#EEF2F7', borderRadius: '5px', padding: '3px 9px' },
  versions:    { fontSize: '10.5px', color: c.textFaint, marginTop: '8px' },
  unlinked:    { fontSize: '10.5px', color: c.warningText, fontWeight: 400 },
  warnBlock:   { background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 14px', marginBottom: '18px' },
  warnHead:    { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: c.warningText, marginBottom: '5px' },
  warnList:    { margin: 0, paddingLeft: '18px' },
  warnItem:    { fontSize: '11.5px', color: c.warningText, lineHeight: 1.6 },
  warnFoot:    { fontSize: '10.5px', color: c.warningText, marginTop: '6px' },
  tableWrap:   { border: `1px solid ${c.border}`, borderRadius: '8px', overflow: 'auto' },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: '11.5px' },
  th:          { padding: '8px 10px', background: c.surfaceSunk, borderBottom: `1px solid ${c.border}`, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, whiteSpace: 'nowrap', textAlign: 'left' },
  thR:         { padding: '8px 10px', background: c.surfaceSunk, borderBottom: `1px solid ${c.border}`, fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: c.textMuted, whiteSpace: 'nowrap', textAlign: 'right' },
  tr:          { borderBottom: `1px solid ${c.canvas}` },
  td:          { padding: '7px 10px', color: c.textStrong, whiteSpace: 'nowrap', borderBottom: `1px solid ${c.canvas}` },
  tdR:         { padding: '7px 10px', color: c.textStrong, whiteSpace: 'nowrap', textAlign: 'right', borderBottom: `1px solid ${c.canvas}`, fontVariantNumeric: 'tabular-nums' },
  actionRow:   { display: 'flex', gap: '10px', marginTop: '6px' },
  logSection:  { ...card.sm, overflow: 'hidden' },
  logTitle:    { fontSize: '13px', fontWeight: 600, color: c.text, padding: '13px 16px', borderBottom: `1px solid ${c.border}`, background: c.surfaceSunk },
  statusPill:  { display: 'inline-flex', padding: '2px 8px', borderRadius: '10px', fontSize: '10.5px', fontWeight: 500 },
  emptyLog:    { textAlign: 'center', padding: '24px', color: c.textFaint },
};
